import { z } from "zod";
import { UuidSchema } from "./common.js";
/**
 * VOTE — le contrat partagé BFF ↔ web pour le hub de vote (Etheria_Vote via Etheria_WebAPI).
 *
 * Le plugin Etheria_Vote expose (à travers Etheria_WebAPI) :
 *   GET  /api/read/vote/status?uuid=|name=  → statut personnel (streak / palier / party)
 *   GET  /api/read/vote/top?limit=          → top mensuel
 *   GET  /api/wiki/vote/sites               → sites de vote + config Votifier
 *   POST /api/action/vote/site-claim        → réclamer le bonus « vote depuis le site »
 *
 * DÉGRADATION GRACIEUSE (game06 hors ligne / WebAPI non configurée) : le BFF renvoie un statut
 * « offline » (`online:false`, champs nuls) plutôt qu'une erreur — la page reste utilisable
 * (les sites de vote sont servis, seules les stats live passent en « indisponibles »).
 */
/* ────────────────────────────────────────────────────────────────────────────
 * Sites de vote (catalogue public — caché comme le wiki).
 * ──────────────────────────────────────────────────────────────────────────── */
/** Un listing de vote (top-serveurs, serveur-prive…) avec son URL de vote. */
export const VoteSiteSchema = z.object({
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(120),
    /** URL externe où le joueur vote (peut être « # » tant que non configurée). */
    url: z.string().min(1).max(512),
});
/** Config Votifier exposée à titre informatif (host/port/protocoles). */
export const VotifierInfoSchema = z.object({
    enabled: z.boolean(),
    host: z.string().nullable(),
    port: z.number().int().nullable(),
    protocols: z.array(z.string()),
});
/** Réponse de `GET /api/vote/sites` — catalogue de vote + Votifier. */
export const VoteSitesResSchema = z.object({
    sites: z.array(VoteSiteSchema),
    votifier: VotifierInfoSchema,
});
/* ────────────────────────────────────────────────────────────────────────────
 * Statut personnel de vote (streak / palier / party).
 * ──────────────────────────────────────────────────────────────────────────── */
/** Progression de la Vote Party (compte courant vs cible). */
export const VotePartySchema = z.object({
    count: z.number().int().nonnegative(),
    target: z.number().int().positive(),
    /** ratio borné 0..1 (count/target). */
    ratio: z.number().min(0).max(1),
});
/** Prochain palier cumulé + récompense (null si tous atteints). */
export const VoteMilestoneSchema = z.object({
    votes: z.number().int().positive(),
    /** votes restants avant ce palier. */
    restant: z.number().int().nonnegative(),
    argent: z.number().int().nonnegative(),
    jetons: z.number().int().nonnegative(),
});
/**
 * Statut de vote d'un joueur. `online:false` = dégradation (game06 hors ligne) : tous les
 * compteurs sont neutres (0 / false / null) et la page affiche « stats indisponibles ».
 */
export const VoteStatusSchema = z.object({
    /** true si la WebAPI a répondu (serveur de jeu connecté) ; false = état dégradé. */
    online: z.boolean(),
    total: z.number().int().nonnegative(),
    monthTotal: z.number().int().nonnegative(),
    streakDays: z.number().int().nonnegative(),
    bestStreak: z.number().int().nonnegative(),
    votedToday: z.boolean(),
    /** bonus « vote depuis le site » réclamable aujourd'hui. */
    siteBonusAvailable: z.boolean(),
    party: VotePartySchema,
    nextMilestone: VoteMilestoneSchema.nullable(),
});
/* ────────────────────────────────────────────────────────────────────────────
 * Top mensuel (classement des voteurs).
 * ──────────────────────────────────────────────────────────────────────────── */
/** Une ligne du classement mensuel de vote. */
export const VoteTopEntrySchema = z.object({
    rank: z.number().int().positive(),
    uuid: UuidSchema.nullable(),
    name: z.string(),
    votes: z.number().int().nonnegative(),
});
/** Réponse de `GET /api/vote/top` — top mensuel (dégrade en liste vide hors ligne). */
export const VoteTopResSchema = z.object({
    online: z.boolean(),
    top: z.array(VoteTopEntrySchema),
});
/* ────────────────────────────────────────────────────────────────────────────
 * Réclamation du bonus site (POST — auth + compte LIÉ requis).
 * ──────────────────────────────────────────────────────────────────────────── */
/** Récompense délivrée par une réclamation réussie. */
export const VoteClaimRewardSchema = z.object({
    argent: z.number().int().nonnegative(),
    caisse: z.string().nullable(),
});
/** Réponse de `POST /api/vote/site-claim` — succès (le corps est vide côté client : self-scopé). */
export const VoteClaimResSchema = z.object({
    ok: z.literal(true),
    delivered: z.boolean(),
    reward: VoteClaimRewardSchema,
});
//# sourceMappingURL=vote.js.map